/* LM Community feed — reactions and comments.
 *
 * The feed itself is owner-published Shopify metaobjects rendered by Liquid.
 * This file only adds the member-written half on top, keyed by the
 * data-post-key each card carries.
 *
 * Progressive enhancement is deliberate: if the API is slow, unreachable, or
 * not configured, the feed still renders exactly as it does today — the
 * engagement bar simply never appears. The feed must never depend on us.
 */
(function () {
  'use strict';

  var root = document.getElementById('lm-community');
  if (!root || !window.LMAuth) return;

  var API = (root.dataset.api || '').replace(/\/$/, '');
  var LOGIN_URL = root.dataset.loginUrl || '/account/login';
  if (!API) return;

  var LMAuth = window.LMAuth.init({
    api: API,
    googleClientId: root.dataset.googleClientId || '',
  });

  var REACTIONS = ['❤️', '👍', '🐟', '😮'];

  /** postKey → engagement, so a reaction toggle repaints without a refetch. */
  var engagement = {};
  var activeKey = null;

  // --- DOM helpers --------------------------------------------------------

  function el(tag, opts, children) {
    var node = document.createElement(tag);
    opts = opts || {};
    if (opts.className) node.className = opts.className;
    if (opts.text != null) node.textContent = String(opts.text);
    if (opts.attrs) {
      for (var key in opts.attrs) {
        if (opts.attrs[key] != null) node.setAttribute(key, opts.attrs[key]);
      }
    }
    if (opts.on) {
      for (var evt in opts.on) node.addEventListener(evt, opts.on[evt]);
    }
    (children || []).forEach(function (child) {
      if (child) node.appendChild(child);
    });
    return node;
  }

  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  function initials(name) {
    return (name || '?')
      .split(/\s+/)
      .slice(0, 2)
      .map(function (part) {
        return part.charAt(0).toUpperCase();
      })
      .join('');
  }

  function timeAgo(iso) {
    var then = new Date(iso).getTime();
    if (!then) return '';
    var seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (seconds < 60) return 'just now';
    var minutes = Math.floor(seconds / 60);
    if (minutes < 60) return minutes + 'm ago';
    var hours = Math.floor(minutes / 60);
    if (hours < 24) return hours + 'h ago';
    var days = Math.floor(hours / 24);
    if (days < 30) return days + 'd ago';
    return new Date(iso).toLocaleDateString();
  }

  function cards() {
    return Array.prototype.slice.call(root.querySelectorAll('.lmc-card[data-post-key]'));
  }

  // --- reaction bar -------------------------------------------------------

  function paintBar(card) {
    var key = card.dataset.postKey;
    var bar = card.querySelector('.lmc-engage');
    if (!bar) return;

    var data = engagement[key] || { reactions: {}, mine: [], commentCount: 0 };
    clear(bar);

    REACTIONS.forEach(function (emoji) {
      var count = data.reactions[emoji] || 0;
      var mine = data.mine.indexOf(emoji) > -1;

      var button = el(
        'button',
        {
          className: 'lmc-rx' + (mine ? ' on' : ''),
          attrs: {
            type: 'button',
            'aria-pressed': mine ? 'true' : 'false',
            'aria-label': emoji + ' reaction, ' + count,
          },
          on: {
            click: function (event) {
              // Keep the card's own handler from opening the post modal.
              event.stopPropagation();
              toggle(card, emoji, button);
            },
          },
        },
        [
          el('span', { className: 'lmc-rx-em', text: emoji }),
          el('span', { className: 'lmc-rx-n', text: count > 0 ? String(count) : '' }),
        ],
      );
      bar.appendChild(button);
    });

    bar.appendChild(
      el('button', {
        className: 'lmc-cmt-btn',
        attrs: { type: 'button' },
        text:
          data.commentCount === 0
            ? '💬 Comment'
            : '💬 ' + data.commentCount + (data.commentCount === 1 ? ' comment' : ' comments'),
        // No stopPropagation here on purpose: the card's own handler opens the
        // modal, and ours fills in the comments.
      }),
    );
  }

  function toggle(card, emoji, button) {
    if (!LMAuth.isSignedIn()) return promptSignIn(card);
    if (!LMAuth.canPost()) return;

    var key = card.dataset.postKey;
    button.disabled = true;

    LMAuth.api('/api/feed/reactions', { method: 'POST', body: { postKey: key, emoji: emoji } })
      .then(function (payload) {
        engagement[key] = payload.engagement;
        paintBar(card);
      })
      .catch(function () {
        button.disabled = false;
      });
  }

  function promptSignIn(card) {
    // Reacting is the most likely first thing an anonymous reader taps, so it
    // opens the post rather than doing nothing — the comment box inside
    // explains how to sign in.
    activeKey = card.dataset.postKey;
    card.click();
  }

  // --- comments -----------------------------------------------------------

  var modal = document.getElementById('lmcPostModal');
  var panel = null;

  function ensurePanel() {
    if (panel || !modal) return panel;
    var body = modal.querySelector('.lmc-m-body');
    if (!body) return null;

    panel = el('div', { className: 'lmc-cbox' });
    var stock = document.getElementById('lmcMStock');
    // Sits after the stock list but before the WhatsApp call to action.
    if (stock && stock.parentNode === body) body.insertBefore(panel, stock.nextSibling);
    else body.appendChild(panel);
    return panel;
  }

  function renderComments(key) {
    var box = ensurePanel();
    if (!box) return;

    clear(box);
    box.appendChild(el('h3', { text: 'Comments' }));
    var list = el('div', { className: 'lmc-clist' }, [
      el('div', { className: 'lmc-cempty', text: 'Loading…' }),
    ]);
    box.appendChild(list);
    box.appendChild(composer(key, list));

    LMAuth.api('/api/feed/comments?postKey=' + encodeURIComponent(key))
      .then(function (payload) {
        // A second card may have been opened while this was in flight.
        if (activeKey !== key) return;
        paintComments(list, payload.comments, key);
      })
      .catch(function () {
        if (activeKey !== key) return;
        clear(list);
        list.appendChild(
          el('div', { className: 'lmc-cempty', text: 'Comments could not be loaded.' }),
        );
      });
  }

  function paintComments(list, comments, key) {
    clear(list);
    if (!comments || comments.length === 0) {
      list.appendChild(
        el('div', { className: 'lmc-cempty', text: 'No comments yet. Start the conversation.' }),
      );
      return;
    }
    comments.forEach(function (comment) {
      list.appendChild(commentRow(comment, list, key));
    });
  }

  function commentRow(comment, list, key) {
    var avatar = el('span', { className: 'lmc-cmt-av' });
    if (comment.author.avatarUrl) {
      avatar.appendChild(
        el('img', { attrs: { src: comment.author.avatarUrl, alt: '', loading: 'lazy' } }),
      );
    } else {
      avatar.textContent = initials(comment.author.displayName);
    }

    var head = el('div', { className: 'lmc-cmt-head' }, [
      el('span', { className: 'lmc-cmt-name', text: comment.author.displayName }),
      comment.author.isTeam ? el('span', { className: 'lmc-cmt-team', text: 'LM Team' }) : null,
      el('span', { className: 'lmc-cmt-when', text: timeAgo(comment.createdAt) }),
    ]);

    if (LMAuth.isTeam()) {
      head.appendChild(
        el('button', {
          className: 'lmc-cmt-rm',
          attrs: { type: 'button' },
          text: comment.isRemoved ? 'Restore' : 'Remove',
          on: {
            click: function () {
              var action = comment.isRemoved ? 'restore' : 'remove';
              if (action === 'remove' && !window.confirm('Remove this comment?')) return;
              LMAuth.api('/api/feed/comments/' + comment.id, {
                method: 'PATCH',
                body: { action: action },
              }).then(function () {
                refresh(key, list);
              });
            },
          },
        }),
      );
    }

    return el(
      'div',
      { className: 'lmc-cmt' + (comment.isRemoved ? ' lmc-cmt--removed' : '') },
      [avatar, el('div', { className: 'lmc-cmt-main' }, [head, el('div', { className: 'lmc-cmt-body', text: comment.body })])],
    );
  }

  function refresh(key, list) {
    LMAuth.api('/api/feed/comments?postKey=' + encodeURIComponent(key)).then(function (payload) {
      paintComments(list, payload.comments, key);
      return LMAuth.api('/api/feed/engagement?keys=' + encodeURIComponent(key));
    }).then(function (payload) {
      if (!payload) return;
      engagement[key] = payload.engagement[key];
      var card = root.querySelector('.lmc-card[data-post-key="' + key + '"]');
      if (card) paintBar(card);
    });
  }

  function composer(key, list) {
    var me = LMAuth.me();

    if (!me) {
      var prompt = el('div', { className: 'lmc-csignin' }, [
        el('span', { text: 'Sign in to join the conversation.' }),
        el('a', {
          text: 'Use your store account',
          attrs: { href: LOGIN_URL + '?return_url=' + encodeURIComponent(window.location.pathname) },
        }),
      ]);
      if (LMAuth.hasGoogle()) {
        var slot = el('div');
        prompt.appendChild(slot);
        LMAuth.renderGoogleButton(slot, 'signin', function (error) {
          if (!error) renderComments(key);
        });
      }
      return prompt;
    }

    if (!me.canPost) {
      return el('div', {
        className: 'lmc-csignin',
        text: 'Your posting access is paused, so you cannot comment right now. You can still read along.',
      });
    }

    var field = el('textarea', {
      attrs: { maxlength: '1200', placeholder: 'Add a comment…', 'aria-label': 'Add a comment' },
    });
    var submit = el('button', { attrs: { type: 'submit' }, text: 'Post' });
    var error = el('div', { className: 'lmc-cerr' });
    error.hidden = true;

    var form = el(
      'form',
      {
        className: 'lmc-cform',
        on: {
          submit: function (event) {
            event.preventDefault();
            if (!field.value.trim()) return;
            submit.disabled = true;
            error.hidden = true;

            LMAuth.api('/api/feed/comments', {
              method: 'POST',
              body: { postKey: key, body: field.value },
            })
              .then(function (payload) {
                field.value = '';
                submit.disabled = false;
                engagement[key] = payload.engagement;
                var card = root.querySelector('.lmc-card[data-post-key="' + key + '"]');
                if (card) paintBar(card);
                refresh(key, list);
              })
              .catch(function (err) {
                submit.disabled = false;
                error.textContent = err.message;
                error.hidden = false;
              });
          },
        },
      },
      [field, submit],
    );

    return el('div', {}, [error, form]);
  }

  // --- community poll -----------------------------------------------------
  //
  // The sidebar poll card. Owners publish one active poll at a time from the
  // admin; here members see it, vote once, and the card flips to results.
  // Results stay hidden until the viewer has voted (or the poll has closed),
  // which the server enforces — this only draws what it is given.

  var pollBox = null;
  var currentPoll = null;

  function initPoll() {
    pollBox = root.querySelector('[data-live-poll]');
    if (!pollBox) return;
    loadPoll();
  }

  function loadPoll() {
    if (!pollBox) return;
    LMAuth.api('/api/polls')
      .then(function (payload) {
        currentPoll = payload.poll;
        if (!currentPoll) {
          pollBox.hidden = true;
          return;
        }
        renderPoll();
        pollBox.hidden = false;
      })
      .catch(function () {
        // Unreachable API: leave the card hidden rather than showing a broken
        // one. The rest of the page is unaffected.
        pollBox.hidden = true;
      });
  }

  function renderPoll() {
    var poll = currentPoll;
    clear(pollBox);
    pollBox.appendChild(el('div', { className: 'lmc-poll-hd', text: '🗳️ Community Poll' }));
    pollBox.appendChild(el('div', { className: 'lmc-poll-q', text: poll.question }));

    var total = poll.totalVotes || 0;
    var opts = el('div', { className: 'lmc-poll-opts' });

    poll.options.forEach(function (option) {
      if (poll.showResults) {
        opts.appendChild(pollResult(option, total, poll.myOptionId === option.id));
      } else {
        opts.appendChild(pollButton(option));
      }
    });
    pollBox.appendChild(opts);

    var note;
    if (poll.isClosed) note = 'Voting has closed.';
    else if (poll.hasVoted) note = 'Thanks for voting!';
    else if (!LMAuth.isSignedIn()) note = 'Sign in to cast your vote.';
    else if (!LMAuth.canPost()) note = 'Your posting access is paused, so you cannot vote right now.';
    else note = total === 1 ? '1 vote so far.' : total + ' votes so far.';
    pollBox.appendChild(el('p', { className: 'lmc-poll-note', text: note }));
  }

  function pollButton(option) {
    return el('button', {
      className: 'lmc-poll-opt',
      attrs: { type: 'button' },
      text: option.text,
      on: {
        click: function () {
          castVote(option.id);
        },
      },
    });
  }

  function pollResult(option, total, mine) {
    var pct = total > 0 ? Math.round(((option.votes || 0) / total) * 100) : 0;
    return el('div', { className: 'lmc-poll-res' + (mine ? ' mine' : '') }, [
      el('div', { className: 'lmc-poll-res-top' }, [
        el('span', { className: 'lmc-poll-res-lbl', text: option.text + (mine ? ' ✓' : '') }),
        el('span', { className: 'lmc-poll-res-pct', text: pct + '%' }),
      ]),
      el('div', { className: 'lmc-poll-res-track' }, [
        el('div', { className: 'lmc-poll-res-fill', attrs: { style: 'width:' + pct + '%' } }),
      ]),
    ]);
  }

  function castVote(optionId) {
    if (!LMAuth.isSignedIn()) return pollSignIn();
    if (!LMAuth.canPost()) return;

    LMAuth.api('/api/polls/' + currentPoll.id + '/vote', {
      method: 'POST',
      body: { optionId: optionId },
    })
      .then(function (payload) {
        currentPoll = payload.poll;
        renderPoll();
      })
      .catch(function (error) {
        // Voted already elsewhere, or the poll just closed: re-sync to the
        // current state rather than showing a dead-end error.
        if (error.code === 'already_voted' || error.code === 'poll_closed') {
          loadPoll();
          return;
        }
        var existing = pollBox.querySelector('.lmc-cerr');
        if (existing) existing.parentNode.removeChild(existing);
        pollBox.appendChild(el('div', { className: 'lmc-cerr', text: error.message }));
      });
  }

  function pollSignIn() {
    if (pollBox.querySelector('.lmc-poll-signin')) return;
    var row = el('div', { className: 'lmc-poll-signin lmc-csignin' }, [
      el('span', { text: 'Sign in to vote.' }),
      el('a', {
        text: 'Use your store account',
        attrs: { href: LOGIN_URL + '?return_url=' + encodeURIComponent(window.location.pathname) },
      }),
    ]);
    if (LMAuth.hasGoogle()) {
      var slot = el('div');
      row.appendChild(slot);
      LMAuth.renderGoogleButton(slot, 'signin', function (error) {
        if (!error) loadPoll();
      });
    }
    pollBox.appendChild(row);
  }

  // --- wiring -------------------------------------------------------------

  function attach() {
    var list = cards();
    if (list.length === 0) return;

    list.forEach(function (card) {
      if (!card.querySelector('.lmc-engage')) {
        var body = card.querySelector('.lmc-cbody') || card;
        body.appendChild(el('div', { className: 'lmc-engage' }));
      }
      paintBar(card);

      // Registered after the section's own card handler, so by the time this
      // runs the modal is already open and populated.
      card.addEventListener('click', function (event) {
        if (event.target.closest('.lmc-card-actions')) return;
        if (event.target.closest('.lmc-rx')) return;
        activeKey = card.dataset.postKey;
        renderComments(activeKey);
      });
    });

    loadEngagement(list);
  }

  function loadEngagement(list) {
    var keys = list.map(function (card) {
      return card.dataset.postKey;
    });
    if (keys.length === 0) return;

    LMAuth.api('/api/feed/engagement?keys=' + keys.map(encodeURIComponent).join(','))
      .then(function (payload) {
        engagement = payload.engagement || {};
        list.forEach(paintBar);
      })
      .catch(function () {
        // Leave the bars at zero rather than hiding them: the counts are
        // cosmetic, and a member can still react once the API recovers.
      });
  }

  // Re-fetch after sign-in so "mine" highlights the reactions already given,
  // and the poll reflects whether this member has voted.
  LMAuth.onChange(function () {
    loadEngagement(cards());
    loadPoll();
  });

  LMAuth.boot().then(function () {
    attach();
    initPoll();
  });
})();
